import { Flex, ScreenReaderOnly, Text } from '@moonpig/launchpad-components'
import React, { FC } from 'react'
import { IconSystemChevronRight } from '@moonpig/launchpad-assets'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { colorValue } from '@moonpig/launchpad-theme'
import { useFindLocaleText } from '../../../../text-localisation'
import { FILTERS_MENU_HEADER_HEIGHT_PX } from '../../constants'
import { SelectedFiltersList } from '../SelectedFiltersList'
import {
  FilterCategory,
  IntegerFilter,
  FilterItem,
} from '../../../../services/types/services'

const ICON_SIZE = 24

const StyledLabel = styled(Text)`
  display: block;
  ${s({
    textAlign: 'left',
    typography: { xs: 'typeMobileDisplay05', md: 'typeDesktopBodyLabel' },
  })}
`

const StyledButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: ${FILTERS_MENU_HEADER_HEIGHT_PX};
  ${s({
    py: 5,
    px: 6,
    borderBottom: 1,
    borderColor: 'colorBorder04',
    color: 'colorTextLabel',
  })}

  &:hover {
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
    })}
  }

  &:focus,
  &:focus-visible {
    outline-color: ${colorValue('colorInteractionFocusIndicator')};
    ${s({
      bgcolor: 'colorBackground02',
      borderColor: 'colorBlack05',
      color: 'colorInteractionFocusIndicator',
    })}

    &:active {
      ${s({
        bgcolor: 'colorBackground03',
        borderColor: 'colorBlack10',
      })}
    }
  }
`

const StyledButtonTextWrapper = styled(Flex)`
  flex-direction: column;
  flex: 5;
  overflow: hidden;
  justify-content: space-between;
`

const StyledIconWrapper = styled(Flex)`
  flex: 1;
  justify-content: flex-end;
`

const StyledChevronRightIcon = styled(IconSystemChevronRight)`
  flex-shrink: 0;
`

type Props = {
  filter: FilterCategory | IntegerFilter
  onClick: (filter: FilterItem) => void
  pill?: React.ReactNode
}

export const TopLevelFilterButton: FC<Props> = ({ filter, onClick, pill }) => {
  const localiseText = useFindLocaleText()

  return (
    <StyledButton
      data-testid="filter-category"
      onClick={() => onClick(filter)}
      key={filter.id}
      role="tab"
      aria-controls={`${filter.id}-filter-category-panel`}
    >
      <StyledButtonTextWrapper>
        <StyledLabel>{filter.label}</StyledLabel>
        <ScreenReaderOnly>
          {localiseText('find.filters_menu_panel.open', filter.label)}
        </ScreenReaderOnly>
        <SelectedFiltersList filter={filter} />
      </StyledButtonTextWrapper>
      {pill}
      <StyledIconWrapper>
        <StyledChevronRightIcon width={ICON_SIZE} height={ICON_SIZE} />
      </StyledIconWrapper>
    </StyledButton>
  )
}
